import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { FeedbackPrompt } from "@/components/FeedbackPrompt";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, FileText, Check, Gift, ShoppingBag, Loader2, X, ExternalLink, Eye, Sparkles, ArrowRight } from "lucide-react";
import FormPreviewModal from "@/components/FormPreviewModal";

export default function Forms() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [systems, setSystems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [packs, setPacks] = useState([]);
  const [forms, setForms] = useState([]);
  const [activeSystem, setActiveSystem] = useState("all");
  const [activeDept, setActiveDept] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(location.state?.preselect ? [location.state.preselect] : []);
  const [freeClaimed, setFreeClaimed] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [aiQuery, setAiQuery] = useState("");
  const [aiResults, setAiResults] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [captureEmail, setCaptureEmail] = useState("");
  const [captureBusy, setCaptureBusy] = useState(false);
  const [captureSent, setCaptureSent] = useState(false);

  const askAssistant = async (queryOverride) => {
    const q = (typeof queryOverride === "string" ? queryOverride : aiQuery).trim();
    if (q.length < 3) return toast.error("Describe the situation in a few words.");
    if (q !== aiQuery) setAiQuery(q);
    setAiBusy(true);
    setAiResults(null);
    setCaptureSent(false);
    setCaptureEmail("");
    try {
      const { data } = await api.post("/forms/assistant", { query: q }, { timeout: 90000 });
      setAiResults(data.recommendations || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assistant is busy, try again.");
    } finally { setAiBusy(false); }
  };

  const emailMyForms = async () => {
    const email = captureEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Enter a valid email.");
    setCaptureBusy(true);
    try {
      await api.post("/forms/assistant/email", {
        email,
        query: aiQuery.trim(),
        codes: (aiResults || []).map((f) => f.code),
      });
      setCaptureSent(true);
      toast.success("Sent! Check your inbox for your forms.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send, try again.");
    } finally { setCaptureBusy(false); }
  };

  useEffect(() => {
    api.get("/systems").then((r) => setSystems(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/packs").then((r) => setPacks(r.data)).catch(() => {});
    if (user) {
      api.get("/me/free-status").then((r) => setFreeClaimed(r.data.claimed)).catch(() => setFreeClaimed(true));
    } else {
      setFreeClaimed(false);
    }
  }, [user]);

  useEffect(() => {
    const fq = location.state?.finderQuery;
    if (fq) {
      window.history.replaceState({}, document.title);
      askAssistant(fq);
    }
    const dept = new URLSearchParams(location.search).get("dept");
    if (dept) setActiveDept(dept);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeSystem !== "all") params.system = activeSystem;
      if (activeDept !== "all") params.department = activeDept;
      if (query.trim()) params.q = query.trim();
      const { data } = await api.get("/forms", { params });
      setForms(data);
    } catch {
      setForms([]);
    } finally {
      setLoading(false);
    }
  }, [activeSystem, activeDept, query]);

  useEffect(() => {
    const t = setTimeout(fetchForms, query ? 400 : 0);
    return () => clearTimeout(t);
  }, [fetchForms, query]);

  const toggle = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const pack3 = packs.find((p) => p.size === 3);
  const pack5 = packs.find((p) => p.size === 5);
  const count = selected.length;

  const requireAuth = () => {
    if (!user) {
      navigate("/register", { state: { from: "/forms" } });
      return false;
    }
    return true;
  };

  const buyPack = async (pack) => {
    if (!requireAuth()) return;
    if (count !== pack.size) {
      toast.error(`Select exactly ${pack.size} forms for this pack.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/pack", {
        pack_id: pack.id,
        document_ids: selected,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  const claimFree = async () => {
    if (!requireAuth()) return;
    if (count !== 3) {
      toast.error("Pick exactly 3 forms to claim free.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/promo/claim-free", { document_ids: selected });
      toast.success("Your 3 free forms are unlocked! Find them in your portal.");
      navigate("/portal/library");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim free forms.");
      setBusy(false);
    }
  };

  const sysName = (code) => systems.find((s) => s.code === code)?.short || code;
  const canClaimFree = user && !freeClaimed;

  return (
    <div className="pb-32">
      <Seo
        title="Dealership Forms & SOP Library | Centennial"
        description="Browse 300+ ready-to-use dealership forms and SOPs across service, parts, sales, HR, safety and collision. Claim 3 forms free."
        canonicalPath="/forms"
      />
      {/* Header */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-16">
          <span className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold">Form Library</span>
          <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tighter mt-3 max-w-3xl">
            Build your own pack. Pick the exact forms you need.
          </h1>
          <p className="text-white/60 mt-4 max-w-xl text-lg">
            Browse every system, search by keyword, and bundle forms into a 3-pack or 5-pack.
            {canClaimFree && " New here? Pick any 3 and claim them free."}
          </p>
          {canClaimFree && (
            <div className="mt-6 inline-flex items-center gap-2 bg-primary px-4 py-2.5 text-sm font-semibold" data-testid="free-promo-banner">
              <Gift className="w-4 h-4" /> Your welcome gift: choose any 3 forms free
            </div>
          )}
          {!user && (
            <div className="mt-6 inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-2.5 text-sm">
              <Gift className="w-4 h-4" /> Sign up to claim 3 free forms
            </div>
          )}
        </div>
      </section>

      {/* Controls */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-8 py-8">
        {/* AI Form Finder */}
        <div className="border-2 border-primary/20 bg-primary/5 p-5 mb-8" data-testid="form-finder">
          <div className="flex items-center gap-2 text-primary font-semibold">
            <Sparkles className="w-4 h-4" /> <span className="text-sm">Not sure which form? Describe what happened.</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <input
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && askAssistant()}
              placeholder="e.g. a technician backed a customer's car into a pole"
              data-testid="form-finder-input"
              className="flex-1 border border-border px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button onClick={askAssistant} disabled={aiBusy} data-testid="form-finder-submit"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-5 py-3 font-semibold btn-lift disabled:opacity-60 whitespace-nowrap">
              {aiBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Finding…</> : <><Sparkles className="w-4 h-4" /> Find my forms</>}
            </button>
          </div>

          {aiResults !== null && (
            <div className="mt-5" data-testid="form-finder-results">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold">{aiResults.length ? `${aiResults.length} forms for your situation` : "No close match — try rephrasing or browse below."}</span>
                <button onClick={() => { setAiResults(null); setAiQuery(""); }} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="form-finder-clear">
                  <X className="w-3.5 h-3.5" /> Clear
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {aiResults.map((f) => (
                  <div key={f.id} data-testid={`finder-result-${f.code}`} className="bg-white border border-border p-4 flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] bg-secondary px-1.5 py-0.5">{f.code}</span>
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{f.department}</span>
                    </div>
                    <div className="font-semibold text-sm mt-2 leading-snug">{f.title}</div>
                    <p className="text-xs text-muted-foreground mt-1.5 flex-1"><Sparkles className="w-3 h-3 inline mr-1 text-primary" />{f.reason}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <button onClick={() => setPreview(f)} data-testid={`finder-preview-${f.code}`}
                        className="text-xs font-semibold inline-flex items-center gap-1 border border-border px-2.5 py-1.5 hover:border-primary">
                        <Eye className="w-3 h-3" /> Preview
                      </button>
                      <Link to={`/forms/${f.code}`} data-testid={`finder-view-${f.code}`}
                        className="text-xs font-semibold text-primary inline-flex items-center gap-1 hover:gap-1.5 transition-all">
                        View & add <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {aiResults.length > 0 && (
                <div className="mt-4 border border-primary/30 bg-white p-4" data-testid="finder-email-capture">
                  {captureSent ? (
                    <div className="flex items-center gap-2 text-sm font-semibold text-green-700" data-testid="finder-email-sent">
                      <Check className="w-4 h-4" /> Sent! These forms are on their way to your inbox.
                    </div>
                  ) : (
                    <>
                      <div className="text-sm font-semibold">Want these emailed to you?</div>
                      <p className="text-xs text-muted-foreground mt-1">We'll send links to all {aiResults.length} forms so you can grab them later.</p>
                      <div className="flex flex-col sm:flex-row gap-2 mt-3">
                        <input
                          type="email"
                          value={captureEmail}
                          onChange={(e) => setCaptureEmail(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && emailMyForms()}
                          placeholder="you@dealership.com"
                          data-testid="finder-email-input"
                          className="flex-1 border border-border px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        />
                        <button onClick={emailMyForms} disabled={captureBusy} data-testid="finder-email-submit"
                          className="inline-flex items-center justify-center gap-2 bg-primary text-white px-5 py-2.5 font-semibold btn-lift disabled:opacity-60 whitespace-nowrap text-sm">
                          {captureBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Email me these forms"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="relative max-w-xl">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Or search forms by name…"
            data-testid="forms-search"
            className="w-full border border-border pl-10 pr-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="flex flex-wrap gap-2 mt-6">
          <Chip active={activeSystem === "all"} onClick={() => setActiveSystem("all")} testid="system-chip-all">
            All systems
          </Chip>
          {systems.map((s) => (
            <Chip key={s.code} active={activeSystem === s.code} onClick={() => setActiveSystem(s.code)} testid={`system-chip-${s.code}`}>
              {s.code} · {s.short} ({s.count})
            </Chip>
          ))}
        </div>

        {departments.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mr-1">Department</span>
            <Chip active={activeDept === "all"} onClick={() => setActiveDept("all")} testid="dept-chip-all">
              All
            </Chip>
            {departments.map((d) => (
              <Chip key={d.name} active={activeDept === d.name} onClick={() => setActiveDept(d.name)} testid={`dept-chip-${d.name.replace(/\s+/g, "-").toLowerCase()}`}>
                {d.name} ({d.count})
              </Chip>
            ))}
          </div>
        )}

        {/* Forms grid */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>
        ) : forms.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="forms-empty">No forms found. Try another system or search.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {forms.map((f) => {
              const sel = selected.includes(f.id);
              return (
                <div
                  key={f.id}
                  onClick={() => toggle(f.id)}
                  role="button"
                  tabIndex={0}
                  data-testid={`form-card-${f.id}`}
                  className={`cursor-pointer text-left p-5 border transition-colors ${sel ? "border-primary border-2 bg-primary/5" : "border-border bg-white hover:border-primary/40"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="w-9 h-9 bg-secondary flex items-center justify-center shrink-0">
                      <FileText className="w-4 h-4 text-primary" />
                    </div>
                    <div className={`w-6 h-6 flex items-center justify-center border ${sel ? "bg-primary border-primary" : "border-border"}`}>
                      {sel && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                  <h3 className="font-semibold mt-4 leading-snug">{f.title}</h3>
                  <div className="flex items-center justify-between gap-2 mt-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {f.code && <span className="font-mono text-[10px] bg-secondary px-1.5 py-0.5 text-foreground/70 shrink-0">{f.code}</span>}
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground truncate">
                        {sysName(f.system)}{f.department && f.department !== sysName(f.system) ? ` · ${f.department}` : ""}
                      </span>
                    </div>
                    {f.code && (
                      <Link to={`/forms/${f.code}`} onClick={(e) => e.stopPropagation()} data-testid={`form-detail-link-${f.code}`}
                        className="text-muted-foreground hover:text-primary shrink-0" title="View form page">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setPreview(f); }}
                    data-testid={`form-preview-btn-${f.id}`}
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 border border-border text-xs font-semibold py-2 text-foreground/70 hover:border-primary hover:text-primary transition-colors"
                    title="Preview this form"
                  >
                    <Eye className="w-3.5 h-3.5" /> Preview
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Sticky action bar */}
      {count > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-border shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.2)]" data-testid="pack-bar">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="font-heading font-black text-2xl">{count}</span>
              <span className="text-sm text-muted-foreground">
                form{count !== 1 ? "s" : ""} selected
                <button onClick={() => setSelected([])} className="ml-3 text-primary inline-flex items-center gap-1" data-testid="pack-clear">
                  <X className="w-3.5 h-3.5" /> clear
                </button>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {canClaimFree && (
                <button onClick={claimFree} disabled={busy || count !== 3} data-testid="claim-free-btn"
                  className="inline-flex items-center gap-2 border-2 border-primary text-primary px-5 py-2.5 font-semibold disabled:opacity-40 btn-lift">
                  <Gift className="w-4 h-4" /> Claim 3 Free
                </button>
              )}
              {pack3 && (
                <button onClick={() => buyPack(pack3)} disabled={busy || count !== 3} data-testid="buy-pack3-btn"
                  className="inline-flex items-center gap-2 bg-foreground text-white px-5 py-2.5 font-semibold disabled:opacity-40 btn-lift">
                  <ShoppingBag className="w-4 h-4" /> 3-Pack ${pack3.price}
                </button>
              )}
              {pack5 && (
                <button onClick={() => buyPack(pack5)} disabled={busy || count !== 5} data-testid="buy-pack5-btn"
                  className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 font-semibold disabled:opacity-40 btn-lift">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />} 5-Pack ${pack5.price}
                </button>
              )}
            </div>
          </div>
          {count !== 3 && count !== 5 && (
            <div className="bg-secondary text-center text-xs text-muted-foreground py-1.5">
              Select <strong>3 forms</strong> for a 3-pack {canClaimFree ? "or free claim" : ""}, or <strong>5 forms</strong> for a 5-pack.
            </div>
          )}
        </div>
      )}

      {preview && (
        <FormPreviewModal docId={preview.id} title={preview.title} code={preview.code} onClose={() => setPreview(null)} />
      )}
      <FeedbackPrompt />
    </div>
  );
}

function Chip({ active, children, testid, ...props }) {
  return (
    <button {...props} data-testid={testid}
      className={`text-sm px-4 py-2 border transition-colors ${active ? "bg-primary text-white border-primary" : "bg-white border-border hover:border-primary"}`}>
      {children}
    </button>
  );
}
